import { useEffect, useMemo, useState } from 'react';
import { useStepContext } from '../contexts/stepContext';
import { useSubscriptionContext } from '../contexts/subscriptionContext';
import { useTrack } from '../hooks';
import { LocaleText, useMakeLocalizedText } from '../LocaleText';
import { ImagePlaceholderIcon } from '../components/icons';
import { extractYouTubeVideoId } from '../utils/youtube';
import type { CustomContentNode } from '../types';

export const CustomContentStep = ({ step: _step }: { step: CustomContentNode }) => {
  const { dispatch, isPreview } = useStepContext();
  const subscription = useSubscriptionContext();
  const track = useTrack();
  const t = useMakeLocalizedText();

  const [erroredUrl, setErroredUrl] = useState('');

  const imageUrl = t('imageUrl', { fallbackToKey: false }) || '';
  const imageAltText = t('imageAltText', { fallbackToKey: false }) || '';
  const videoUrl = t('videoUrl', { fallbackToKey: false }) || '';
  const videoTitle = t('videoTitle', { fallbackToKey: false }) || '';
  const buttonLink = t('buttonLink', { fallbackToKey: false }) || '';

  const videoId = videoUrl ? extractYouTubeVideoId(videoUrl) : null;
  const mediaType: 'video' | 'image' | null = videoId ? 'video' : imageUrl ? 'image' : null;
  const imageError = mediaType === 'image' && erroredUrl === imageUrl;

  const trackingAttributes = useMemo(
    () => ({
      subscriptionId: subscription.public_id,
      ...(mediaType === 'video' && { videoUrl }),
      ...(mediaType === 'image' && { imageUrl }),
      ...(buttonLink && { buttonLink })
    }),
    [subscription.public_id, mediaType, imageUrl, videoUrl, buttonLink]
  );

  useEffect(() => {
    track('subscriptioncancel_customcontent_shown', trackingAttributes);
  }, [track, trackingAttributes]);

  function handleAccept() {
    track('subscriptioncancel_completed_saved_customcontent', trackingAttributes);
    dispatch({ type: 'accepted' });
  }

  return (
    <div className="og-custom-content-step">
      <div className="og-dialog-body">
        <p>
          <LocaleText t="subheader" />
        </p>

        {mediaType && (
          <div className="og-custom-content-media">
            {mediaType === 'video' ? (
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0`}
                title={videoTitle}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            ) : imageError ? (
              <div className="og-custom-content-media-placeholder">
                <ImagePlaceholderIcon />
              </div>
            ) : (
              <img src={imageUrl} alt={imageAltText} onError={() => setErroredUrl(imageUrl)} />
            )}
          </div>
        )}

        <p>
          <LocaleText t="bodyText" />
        </p>

        <div className="og-custom-content-actions">
          {buttonLink ? (
            <a
              className="og-button"
              data-variant="primary"
              href={buttonLink}
              onClick={e => {
                if (isPreview) e.preventDefault();
                handleAccept();
              }}
            >
              <LocaleText t="acceptButton" />
            </a>
          ) : (
            <button className="og-button" data-variant="primary" onClick={handleAccept}>
              <LocaleText t="acceptButton" />
            </button>
          )}
          <button className="og-button" data-variant="tertiary" onClick={() => dispatch({ type: 'next' })}>
            <LocaleText t="declineButton" />
          </button>
        </div>
      </div>
    </div>
  );
};
